import React, { useState, useContext, ChangeEvent } from 'react';
import Grid from '@mui/material/Grid';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Collapse,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import api from "../api/api";

const AddAccount: React.FC = () => {
  const [tab, setTab] = useState(0);
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const TIMEOUT = 10000;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // ----------- Manual form -----------
  const [name, setName] = useState('');
  const [tgApiHash, setTgApiHash] = useState("");
  const [tgApiId, setTgApiId] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [metaInfo, setMetaInfo] = useState("");

  // ----------- tdata form -----------
  const [tdataFile, setTdataFile] = useState<File | null>(null);
  const [baseName, setBaseName] = useState('');
  const [tdataMetaInfo, setTdataMetaInfo] = useState('');

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
    setError(null);
    setSuccessMessage(null);
  };

  // ================= MANUAL =================

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!auth?.isAuthenticated) {
      setError('Авторизуйтесь');
      return;
    }

    try {
      setIsLoading(true);

      await api.post(
        '/accounts',
        {
          name: name.trim(),
          tg_api_hash: tgApiHash.trim(),
          tg_api_id: tgApiId.trim(),
          phone_number: phoneNumber.trim(),
          meta_info: metaInfo,
        },
        { timeout: TIMEOUT }
      );

      navigate('/accounts');

    } catch (error: any) {
      const data = error.response?.data;
      setError(typeof data === 'string' ? data : data?.detail || error.message || 'Неизвестная ошибка');
    } finally {
      setIsLoading(false);
    }
  };

  // ================= TDATA =================

  const handleTdataImport = async () => {
    setError(null);
    setSuccessMessage(null);

    if (!tdataFile || !baseName.trim()) {
      setError('Выберите ZIP и укажите базовое имя');
      return;
    }

    try {
      setIsLoading(true);

      const formData = new FormData();
      formData.append('tdata_file', tdataFile);
      formData.append('base_name', baseName.trim());

      if (tdataMetaInfo.trim()) {
        formData.append('meta_info', tdataMetaInfo.trim());
      }

      const response = await api.post('/accounts/import/tdata', formData);
      const result = response.data;

      setSuccessMessage(
        `Импорт завершён — Найдено: ${result.total_found}, Успешно: ${result.successfully_imported}, Ошибки: ${result.failed}`
      );

      setTimeout(() => navigate('/accounts'), 2000);

    } catch (error: any) {
      const data = error.response?.data;
      setError(typeof data === 'string' ? data : data?.detail || error.message || 'Неизвестная ошибка');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper sx={{ p: 4 }}>

        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
          Добавление аккаунта
        </Typography>

        <Tabs
          value={tab}
          onChange={handleTabChange}
          sx={{ mb: 4 }}
        >
          <Tab label="Вручную" />
          <Tab label="Импорт tdata" />
        </Tabs>

        <Collapse in={!!error}>
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        </Collapse>

        <Collapse in={!!successMessage}>
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        </Collapse>

        {/* ================= MANUAL TAB ================= */}
        {tab === 0 && (
          <form onSubmit={handleManualSubmit}>
            <Grid container spacing={2}>

              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Имя"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  required
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  label="TG API Hash"
                  value={tgApiHash}
                  onChange={(e) => setTgApiHash(e.target.value)}
                  fullWidth
                  required
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  label="TG API ID"
                  value={tgApiId}
                  onChange={(e) => setTgApiId(e.target.value)}
                  fullWidth
                  required
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Номер телефона"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  fullWidth
                  required
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Общая информация, примечания"
                  value={metaInfo}
                  onChange={(e) => setMetaInfo(e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={isLoading}
                  sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Добавить'}
                </Button>
              </Grid>

            </Grid>
          </form>
        )}

        {/* ================= TDATA TAB ================= */}
        {tab === 1 && (
          <Box>

            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mb: 3 }}
            >
              {tdataFile ? tdataFile.name : 'Выберите ZIP-файл'}
              <input
                hidden
                type="file"
                accept=".zip"
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  e.target.files && setTdataFile(e.target.files[0])
                }
              />
            </Button>

            <TextField
              label="Базовое имя"
              value={baseName}
              onChange={(e) => setBaseName(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />

            <TextField
              label="Единая информация"
              value={tdataMetaInfo}
              onChange={(e) => setTdataMetaInfo(e.target.value)}
              fullWidth
              multiline
              rows={2}
              sx={{ mb: 3 }}
            />

            <Button
              variant="contained"
              fullWidth
              onClick={handleTdataImport}
              disabled={isLoading}
              sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Импортировать'}
            </Button>

          </Box>
        )}

      </Paper>
    </Container>
  );
};

export default AddAccount;